/**
 * SignaturePad — shared component used on SignPage and CountersignModal.
 *
 * Two modes:
 *   Draw — freehand canvas signature
 *   Type — type name, pick a cursive style, rendered to canvas data URL
 */
import React, { useEffect, useRef, useState } from 'react';
import { PenLine, Type } from 'lucide-react';

// ── Cursive fonts loaded from Google Fonts ────────────────────────────────────

const FONTS = [
  { name: 'Dancing Script', family: 'Dancing Script', size: 52 },
  { name: 'Great Vibes',    family: 'Great Vibes',    size: 58 },
  { name: 'Caveat',         family: 'Caveat',          size: 56 },
];

const CANVAS_W = 432;
const CANVAS_H = 130;

function loadGoogleFonts() {
  if (document.getElementById('sig-fonts')) return;
  const link = document.createElement('link');
  link.id = 'sig-fonts';
  link.rel = 'stylesheet';
  link.href = 'https://fonts.googleapis.com/css2?family=Dancing+Script:wght@600&family=Great+Vibes&family=Caveat:wght@600&display=swap';
  document.head.appendChild(link);
}

async function renderTypedSignature(name, font) {
  await document.fonts.ready;
  const canvas = document.createElement('canvas');
  canvas.width = CANVAS_W;
  canvas.height = CANVAS_H;
  const ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);
  ctx.fillStyle = '#16222C';
  ctx.font = `${font.size}px "${font.family}"`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(name, CANVAS_W / 2, CANVAS_H / 2);
  return canvas.toDataURL('image/png');
}

// ── SignaturePad ──────────────────────────────────────────────────────────────

export default function SignaturePad({ onDone, onCancel, primaryColor = '#A62B1F' }) {
  const [mode, setMode] = useState('draw'); // 'draw' | 'type'

  // Draw state
  const canvasRef = useRef(null);
  const isDrawing = useRef(false);
  const [hasStroke, setHasStroke] = useState(false);

  // Type state
  const [typedName, setTypedName] = useState('');
  const [selectedFont, setSelectedFont] = useState(0);
  const [previews, setPreviews] = useState({});

  useEffect(() => { loadGoogleFonts(); }, []);

  // ── Canvas drawing setup ──────────────────────────────────────────────────

  useEffect(() => {
    if (mode !== 'draw') return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#16222C';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    const pos = (e) => {
      const r = canvas.getBoundingClientRect();
      const s = e.touches ? e.touches[0] : e;
      return {
        x: (s.clientX - r.left) * (canvas.width / r.width),
        y: (s.clientY - r.top) * (canvas.height / r.height),
      };
    };
    const start = (e) => { e.preventDefault(); isDrawing.current = true; const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
    const move  = (e) => { e.preventDefault(); if (!isDrawing.current) return; const p = pos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); setHasStroke(true); };
    const end   = () => { isDrawing.current = false; };

    canvas.addEventListener('mousedown', start);
    canvas.addEventListener('mousemove', move);
    canvas.addEventListener('mouseup', end);
    canvas.addEventListener('mouseleave', end);
    canvas.addEventListener('touchstart', start, { passive: false });
    canvas.addEventListener('touchmove', move, { passive: false });
    canvas.addEventListener('touchend', end);
    return () => {
      canvas.removeEventListener('mousedown', start);
      canvas.removeEventListener('mousemove', move);
      canvas.removeEventListener('mouseup', end);
      canvas.removeEventListener('mouseleave', end);
      canvas.removeEventListener('touchstart', start);
      canvas.removeEventListener('touchmove', move);
      canvas.removeEventListener('touchend', end);
    };
  }, [mode]);

  const clearCanvas = () => {
    const c = canvasRef.current;
    if (c) c.getContext('2d').clearRect(0, 0, c.width, c.height);
    setHasStroke(false);
  };

  // ── Typed preview rendering ───────────────────────────────────────────────

  useEffect(() => {
    if (!typedName.trim()) { setPreviews({}); return; }
    let active = true;
    Promise.all(FONTS.map(f => renderTypedSignature(typedName, f))).then(results => {
      if (!active) return;
      const map = {};
      FONTS.forEach((f, i) => { map[i] = results[i]; });
      setPreviews(map);
    });
    return () => { active = false; };
  }, [typedName]);

  // ── Apply ─────────────────────────────────────────────────────────────────

  const handleApply = async () => {
    if (mode === 'draw') {
      const dataUrl = canvasRef.current?.toDataURL('image/png');
      if (dataUrl) onDone(dataUrl);
    } else {
      if (!typedName.trim()) return;
      const dataUrl = await renderTypedSignature(typedName, FONTS[selectedFont]);
      onDone(dataUrl);
    }
  };

  const canApply = mode === 'draw' ? hasStroke : (!!typedName.trim() && !!previews[selectedFont]);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 100,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}>
      <div style={{ background: '#fff', borderRadius: 14, padding: 24, width: '100%', maxWidth: 500, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <span style={{ fontWeight: 700, color: '#16222C', fontSize: 16 }}>Your Signature</span>
          <button onClick={onCancel} style={{ color: '#9AA3AB', background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, lineHeight: 1 }}>×</button>
        </div>

        {/* Mode tabs */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          {[
            { id: 'draw', label: 'Draw', Icon: PenLine },
            { id: 'type', label: 'Type', Icon: Type },
          ].map(({ id, label, Icon }) => (
            <button
              key={id}
              onClick={() => setMode(id)}
              style={{
                flex: 1, padding: '8px 0', borderRadius: 8, border: 'none', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                fontWeight: 600, fontSize: 13, transition: 'all 0.15s',
                background: mode === id ? primaryColor : '#F1ECE3',
                color: mode === id ? '#fff' : '#64748b',
              }}
            >
              <Icon size={14} /> {label}
            </button>
          ))}
        </div>

        {/* Draw mode */}
        {mode === 'draw' && (
          <>
            <div style={{ marginBottom: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 12, color: '#9AA3AB' }}>Draw your signature below</span>
              <button onClick={clearCanvas} style={{ fontSize: 12, color: '#9AA3AB', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>Clear</button>
            </div>
            <canvas
              ref={canvasRef}
              width={CANVAS_W}
              height={CANVAS_H}
              style={{ width: '100%', height: CANVAS_H, border: '1.5px dashed #D3CCBE', borderRadius: 8, background: '#FBF8F2', cursor: 'crosshair', display: 'block', touchAction: 'none' }}
            />
          </>
        )}

        {/* Type mode */}
        {mode === 'type' && (
          <>
            <input
              type="text"
              value={typedName}
              onChange={e => setTypedName(e.target.value)}
              placeholder="Type your full name"
              autoFocus
              style={{
                width: '100%', padding: '10px 12px', borderRadius: 8, border: '1.5px solid #E3DDD1',
                fontSize: 15, color: '#16222C', outline: 'none', marginBottom: 14, boxSizing: 'border-box',
              }}
            />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {FONTS.map((font, i) => (
                <button
                  key={i}
                  onClick={() => setSelectedFont(i)}
                  style={{
                    border: `2px solid ${selectedFont === i ? primaryColor : '#E3DDD1'}`,
                    borderRadius: 8, padding: '12px 16px', cursor: 'pointer', background: selectedFont === i ? `${primaryColor}0f` : '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', transition: 'all 0.15s',
                  }}
                >
                  {previews[i] ? (
                    <img src={previews[i]} alt={font.name} style={{ height: 44, maxWidth: '75%', objectFit: 'contain' }} />
                  ) : (
                    <span style={{ fontFamily: font.family, fontSize: font.size * 0.7, color: '#16222C' }}>
                      {typedName || 'Your Name'}
                    </span>
                  )}
                  <span style={{ fontSize: 11, color: '#9AA3AB', fontFamily: 'system-ui' }}>{font.name}</span>
                </button>
              ))}
            </div>
          </>
        )}

        {/* Audit notice */}
        <p style={{ fontSize: 11, color: '#9AA3AB', marginTop: 12, lineHeight: 1.5 }}>
          By applying your signature you agree this constitutes a legally binding electronic signature. Date, time, and IP address will be recorded.
        </p>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
          <button onClick={onCancel} style={{ flex: 1, padding: '10px 0', border: '1.5px solid #E3DDD1', borderRadius: 8, background: '#fff', color: '#64748b', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>
            Cancel
          </button>
          <button
            onClick={handleApply}
            disabled={!canApply}
            style={{
              flex: 2, padding: '10px 0', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 14,
              background: canApply ? primaryColor : '#E3DDD1',
              color: canApply ? '#fff' : '#9AA3AB',
              cursor: canApply ? 'pointer' : 'not-allowed', transition: 'all 0.15s',
            }}
          >
            Apply Signature
          </button>
        </div>
      </div>
    </div>
  );
}
